import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { Container } from '@mui/system';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { url } from 'src/Helpers/utils/Constant';
import { ISingleRenewalApplication } from 'src/Types/module/applications/applications';
import { getStatusLabel } from 'src/Utils/Combined';
import UpdateModal from './UpdateModal';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from 'src/components/pdfAndImageView/CommonTypoGraphy';
import OfflinePaymentCardInformation from '../../subModules/invoice/singleInvoice/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';

const labelWidth = { xs: '130px', sm: '150px', md: '175px' };

const SingleRenewalApplication = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { id } = useParams();
  const [application, setApplication] =
    useState<ISingleRenewalApplication | null>(null);
  const navigate = useNavigate();
  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    (async () => {
      try {
        const data = await axios.get(
          `${url}/api/member/renewal-application/${id}`
        );
        if (data.data.success) {
          setApplication(data.data.data);
        }
      } catch (err) {
        alert('No application found');
        navigate('/update-applications/renewal-application');
      }
    })();
  }, [id]);

  const applicationDate = formatDate(
    application?.renewal_application_created_at,
    'MMM Do YYYY'
  );

  const statusLabel = application?.renewal_application_status
    ? getStatusLabel(application.renewal_application_status)
    : 'N/A';

  const civilAviationIssueDate = formatDate(
    application?.civil_aviation_issue_date,
    'MMM Do YYYY'
  );

  const civilAviationExpiryDate = formatDate(
    application?.civil_aviation_expiry_date,
    'MMM Do YYYY'
  );

  const civilAviationFile = application?.renewal_application_updated_civil_aviation_file ? (
    <CommonTypoGraphy
      name='View Civil Aviation'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.renewal_application_updated_civil_aviation_file
        );
      }}
    />
  ) : (
    'N/A'
  );

  const tradeLicenseIssueDate = formatDate(
    application?.trade_license_issue_date,
    'MMM Do YYYY'
  );

  const tradeLicenseExpiryDate = formatDate(
    application?.trade_license_expiry_date,
    'MMM Do YYYY'
  );

  const tradeLicenseFile = application?.renewal_application_updated_trade_license_file ? (
    <CommonTypoGraphy
      name='View Trade License'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.renewal_application_updated_trade_license_file
        );
      }}
    />
  ) : (
    'N/A'
  );

  const nidFile = application?.nid_file ? (
    <CommonTypoGraphy
      name='View NID File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.nid_file);
      }}
    />
  ) : (
    'N/A'
  );

  const renewalYearsValue = application?.renewal_years ? (
    <>
      {application.renewal_years.split('-')?.map((year) => (
        <Chip
          size='small'
          key={year}
          label={year}
          color='info'
          variant='filled'
          sx={{ mr: '2px' }}
        />
      ))}
    </>
  ) : (
    'N/A'
  );

  const taxReturnFile = application?.renewal_application_tax_return_certificate_file ? (
    <CommonTypoGraphy
      name='View Tax Return Certificate'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.renewal_application_tax_return_certificate_file
        );
      }}
    />
  ) : (
    'N/A'
  );

  const prevCertificateFile = application?.renewal_application_prev_atab_certificate_file ? (
    <CommonTypoGraphy
      name='View Previous Certificate'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(
          application.renewal_application_prev_atab_certificate_file
        );
      }}
    />
  ) : (
    'N/A'
  );

  const undertakingFile = application?.under_taking_file ? (
    <CommonTypoGraphy
      name='View Undertaking File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.under_taking_file);
      }}
    />
  ) : (
    'N/A'
  );

  const officeDeedFile = application?.office_deed_file ? (
    <CommonTypoGraphy
      name='View Office Deed File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.office_deed_file);
      }}
    />
  ) : (
    'N/A'
  );

  const ltdDocsFile = application?.ltd_docs ? (
    <CommonTypoGraphy
      name='View Ltd. Co. Docs File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.ltd_docs);
      }}
    />
  ) : (
    'N/A'
  );

  const scheduleFile = application?.schedule_10_12 ? (
    <CommonTypoGraphy
      name='View Schedule 10/12 File'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.schedule_10_12);
      }}
    />
  ) : (
    'N/A'
  );

  return (
    <>
      <Helmet>
        <title>Atab - Renewal application</title>
      </Helmet>
      <Container maxWidth='xl'>
        <Card>
          <CardHeader
            title='Renewal application'
            action={
              <Button onClick={() => setShowModal(true)} variant='outlined'>
                Update
              </Button>
            }
          />
          <Divider />
          <CardContent>
            {application ? (
              <Grid container spacing={2} pt={2}>
                <CommonDetailItem
                  label='Application Date'
                  value={applicationDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Company name'
                  value={formatValue(application?.user_member_company_name)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Status'
                  value={statusLabel}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Civil Aviation No'
                  value={formatValue(application?.civil_aviation_number)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='CA Issue Date'
                  value={civilAviationIssueDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='CA Expiry Date'
                  value={civilAviationExpiryDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Civil Aviation File'
                  value={civilAviationFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Trade License No'
                  value={formatValue(application?.trade_license_number)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='TL Issue Date'
                  value={tradeLicenseIssueDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='TL Expiry Date'
                  value={tradeLicenseExpiryDate}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Trade License File'
                  value={tradeLicenseFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='TIN Number'
                  value={formatValue(application?.tin_number)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='NID Number'
                  value={formatValue(application?.nid_no)}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='NID File'
                  value={nidFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Renewal Years'
                  value={renewalYearsValue}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Tax Return File'
                  value={taxReturnFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Prev Cert File'
                  value={prevCertificateFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Undertaking File'
                  value={undertakingFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Office Deed File'
                  value={officeDeedFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Ltd Docs File'
                  value={ltdDocsFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Schedule 10/12'
                  value={scheduleFile}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={6}
                  md={4}
                />
                <CommonDetailItem
                  label='Reject Reason / Note'
                  value={formatValue(
                    application?.renewal_application_reject_reason
                  )}
                  labelWidth={labelWidth}
                  xs={12}
                  sm={12}
                  md={12}
                />

                {application?.paymentInfo && (
                  <Grid item xs={12} mt={2}>
                    <Divider
                      sx={{
                        position: 'relative',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Chip
                        label='Payment Information'
                        size='small'
                        color='primary'
                      />
                      <Link
                        to={`/invoices/transactions/${application?.paymentInfo?.invoice_id}`}
                      >
                        <Chip
                          label='Go Invoice'
                          size='small'
                          sx={{
                            position: 'absolute',
                            right: 0,
                            background: '#D7E9FD',
                          }}
                        />
                      </Link>
                    </Divider>

                    <OfflinePaymentCardInformation
                      data={application?.paymentInfo}
                    />
                  </Grid>
                )}
              </Grid>
            ) : (
              <Typography
                variant='body1'
                fontWeight='bold'
                color='text.primary'
                display='inline'
                sx={{ pr: 1 }}
                gutterBottom
              >
                No Data found
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>

      {application && (
        <UpdateModal
          loading={loading}
          setShowModal={setShowModal}
          showModal={showModal}
          setLoading={setLoading}
          application={application}
          setApplication={setApplication}
        />
      )}

      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />
    </>
  );
};

export default SingleRenewalApplication;
